import React, { useEffect, useRef, useState } from "react";
import Loader from "../../Components/Common/Loader";

/**
 * Defers mounting children until delay elapses (and optionally until near viewport).
 * eager=true mounts after delay without waiting for IntersectionObserver.
 */
const LazySection = ({
  children,
  rootMargin = "200px 0px",
  delayMs = 0,
  minHeight = 180,
  className = "",
  enabled = true,
  eager = false,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setReady(false);
      setVisible(false);
      return undefined;
    }
    if (delayMs <= 0) {
      setReady(true);
      return undefined;
    }
    const timer = setTimeout(() => setReady(true), delayMs);
    return () => clearTimeout(timer);
  }, [delayMs, enabled]);

  useEffect(() => {
    if (!enabled || !ready) {
      return undefined;
    }
    if (eager) {
      setVisible(true);
      return undefined;
    }
    if (visible) {
      return undefined;
    }

    const node = ref.current;
    if (!node) {
      return undefined;
    }

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [ready, visible, rootMargin, enabled, eager]);

  return (
    <div ref={ref} className={className} style={{ minHeight: visible ? undefined : minHeight }}>
      {!enabled || !ready || !visible ? <Loader /> : children}
    </div>
  );
};

export default LazySection;
